import React, { useEffect, useState } from "react";
import {
  MdErrorOutline,
  MdInfoOutline,
  MdWarningAmber,
} from "react-icons/md";
import { useTranslation } from "react-i18next";

// Full class names in an object lookup so Tailwind's JIT can see them.
const TONES = {
  danger: { bg: "bg-state-danger-bg", fg: "text-state-danger", Icon: MdErrorOutline },
  warning: {
    bg: "bg-state-warning-bg",
    fg: "text-state-warning-text",
    Icon: MdWarningAmber,
  },
  info: { bg: "bg-state-info-bg", fg: "text-state-info-text", Icon: MdInfoOutline },
};

const ActionButton = ({ action, variant }) => {
  if (!action) return null;
  const className =
    variant === "primary"
      ? "flex h-11 w-full items-center justify-center rounded-md bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
      : "mt-2 flex h-11 w-full items-center justify-center rounded-md border border-ui-border bg-ui-surface text-sm font-semibold text-ui-text transition-colors hover:bg-ui-surface-sunken";

  if (action.href) {
    return (
      <a href={action.href} className={className}>
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
};

/**
 * Every terminal LOCAL state: fatal parameter errors, the framed-page refusal,
 * and protocol errors that show a countdown before the parent redirects.
 *
 * HARD RULE: callers pass only already-sanitised values. `detail` receives at
 * most a host from safeHost(), never a raw redirect_uri, state, client_id or a
 * backend error_description -- that is what stops a crafted /authorize link
 * painting attacker-chosen copy onto a hajexbolt.com page.
 */
const OAuthErrorCard = ({
  tone = "danger",
  title,
  message,
  detail,
  primaryAction,
  secondaryAction,
  countdownSeconds,
}) => {
  const { t } = useTranslation("auth");
  const { bg, fg, Icon } = TONES[tone] || TONES.danger;
  const hasCountdown = typeof countdownSeconds === "number";
  const [remaining, setRemaining] = useState(countdownSeconds);

  useEffect(() => {
    if (!hasCountdown) return undefined;
    setRemaining(countdownSeconds);
    const id = setInterval(() => {
      setRemaining((value) => (value > 0 ? value - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [hasCountdown, countdownSeconds]);

  return (
    <div className="p-6 text-center">
      <div
        className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${bg}`}
      >
        <Icon size={28} className={fg} aria-hidden="true" />
      </div>

      <h1 className="mb-2 text-[18px] font-bold text-ui-text">{title}</h1>
      {message && (
        <p className="text-[13px] leading-relaxed text-ui-text-muted">
          {message}
        </p>
      )}

      {detail && (
        <p className="mt-4 break-words rounded-lg border border-ui-border bg-ui-surface-sunken px-3 py-2 text-left text-[11px] text-ui-text-muted">
          {detail}
        </p>
      )}

      {hasCountdown && (
        <p className="mt-3 text-[11px] text-ui-text-subtle" aria-live="polite">
          {t("oauth.returningIn", { seconds: remaining })}
        </p>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="mt-6">
          <ActionButton action={primaryAction} variant="primary" />
          <ActionButton action={secondaryAction} variant="secondary" />
        </div>
      )}
    </div>
  );
};

export default OAuthErrorCard;
